import { useForm } from "react-hook-form";
import { Button, Stack, Typography } from "@mui/material";
import LassoStepper from "../../components/LassoStepper";
import { useState } from 'react';
import { useNavigate } from "react-router-dom";
import DynamicForm from "../../components/dynamicform/DynamicForm";
import { Box } from "@mui/system";
import { useSubmitDOIMutation, useSubmitPublicationMutation } from "../../api/publicationApiSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectForms, setFormStudy } from "./publicationSlice";
import { useUserInfoQuery } from "../../api/userApiSlice";
import React from "react";
import { setNewNotification } from "../common/notificationSlice";

const stepLabels = ["Pre-registration", "Registration", "ABCD Information", "HBCD Information"];

const NewPublication = () => {
    const form = useForm({});
    const [step, setStep] = useState(0);
    const [submitDOI, { isLoading: isDOILoading }] = useSubmitDOIMutation();
    const [submitPub] = useSubmitPublicationMutation();
    const { data: userData } = useUserInfoQuery({});
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const forms = useSelector(selectForms);
    const fillRegistrationForm = (doiInfo: any) => {
        Object.entries(doiInfo).forEach(
            ([name, value]) => {
                if (name === 'doi') {
                    form.setValue("doiUrl", value || "")
                    return
                }
                form.setValue(name, value || "")
            });
    }

    const handleStepChange = async (direction: "back" | "next") => {
        if (direction === "back") {
            handleBack();
        } else {
            handleNext();
        }
    }

    const handleBack = () => {
        if (step === 0) {
            navigate("/publications");
        } else {
            setStep((step - 1) % stepLabels.length);
        }
    }

    const handleNext = async () => {
        switch (step) {
        case 0:
            submitStepOne();
            return;
        case 1:
            submitStepTwo();
            return;
        case 2:
            submitStepThree();
            return;
        case 3:
            submitStepFour();
            return;
        }
    }

    function recursiveValidateDoiUrl(doiUrl: string): string {
        const regex = /[^A-Za-z0-9]$/;
        if (doiUrl.trim().length > 0 && regex.test(doiUrl.trim())) {
            doiUrl = doiUrl.slice(0, -1);
            return recursiveValidateDoiUrl(doiUrl);
        }
        return doiUrl.trim()
    }

    const submitStepOne = () => {
        form.handleSubmit(({ didPreregister, doiUrl }) => {
            const noDoiUrlWhenPreregistered = didPreregister.value === "yes" && doiUrl === "";
            if (noDoiUrlWhenPreregistered) {
                form.setError('doiUrl', {
                    type: 'required',
                    message: 'DOI URL required for pre-registration',
                });
            } else if (didPreregister.value === "yes") {
                const fixedDoiUrl = recursiveValidateDoiUrl(doiUrl);
                submitDOI({ data: fixedDoiUrl })
                    .then((response) => {
                        if ('error' in response) {
                            /* This ignore is due to response not being either a FetchBaseQueryError
                            or a SerializedError type. Not sure why the data shape of an error response
                            is different than the expected type.*/
                            //@ts-ignore
                            const errorMessage = response.error.data.error;
                            throw new Error(errorMessage);
                        } else {
                            //@ts-ignore
                            fillRegistrationForm({ ...response?.data, owner: userData.username })

                        }
                    })
                    .then(() => setStep((step + 1) % stepLabels.length))
                    .catch((error) => {
                        form.setError('doiUrl', {
                            type: 'required',
                            message: error.message,
                        }
                        )
                    }
                    );
            } else {
                setStep((step + 1) % stepLabels.length);
            }
        })()
    }

    const submitStepTwo = () => {
        form.handleSubmit(({ study }) => {
            dispatch(setFormStudy(study))
            setStep((step + 1) % stepLabels.length);
        })()
    }

    const submitStepThree = () => {
        form.handleSubmit(() => {
            setStep((step + 1) % stepLabels.length);
        })()
    }

    const submitStepFour = () => {
        form.handleSubmit((data) => {
            const body = { ...data, owner: userData.username };
            submitPub({ data: body }).then((response) => {
                if ('error' in response) {
                    dispatch(setNewNotification({
                        message: "There was an error while creating your new publication",
                        type: 'error'
                    }))
                    return
                }
                navigate("/publications")
            })
        })()
    }

    return (
        <Stack
            flexDirection="row"
            gap={4}
            height="100%"
            alignItems="flex-start"
        >
            <Button
                sx={{ mt: 4 }}
                variant={"tertiary"}
                onClick={() => handleStepChange("back")}
            >
                Back
            </Button>
            <Stack flexBasis="100%" gap={4} marginX={5}>
                <Typography
                    variant={"h1"}
                    fontSize={"48px"}
                    textAlign="center"
                    fontWeight={600}
                >
                    Registration of Publication
                </Typography>
                <LassoStepper activeStep={step} stepLabels={stepLabels}/>
                <Box>
                    <DynamicForm form={form} schema={forms[step]}/>
                </Box>

            </Stack>
            <Button
                sx={{ mt: 4 }}
                variant={"primary"}
                onClick={() => handleStepChange("next")}
                disabled={isDOILoading}
            >
                Next
            </Button>

        </Stack>
    )
}

export default NewPublication;